// ═══════════════════════════════════════════════════════════════
// LegalHoldLookupPanel.jsx — SupportDashboard panel for subpoena
// response. Searches data/legalHold by email or original user ID,
// returns the sealed mapping + list of conflicts the user
// participated in. Every lookup writes an audit-log entry server-side
// (in the lookupLegalHold Cloud Function).
//
// "View Conflict" opens LegalHoldConflictView with the original
// identity restored from the legal-hold mapping.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import { Search, Lock, Loader2, AlertCircle, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { STRIPE_CONFIG } from '../shared.jsx';
import LegalHoldConflictView from './LegalHoldConflictView.jsx';

async function callCloudFunction(endpoint, body) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await window.firebaseAuth?.currentUser?.getIdToken(false);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) { /* */ }
  const response = await fetch(`${STRIPE_CONFIG.cloudFunctionUrl}/${endpoint}`, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  return { status: response.status, body: await response.json().catch(() => ({})) };
}

function LegalHoldLookupPanel() {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [originalUserId, setOriginalUserId] = useState('');
  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState('input'); // input | searching | results | error
  const [errorMessage, setErrorMessage] = useState('');
  const [results, setResults] = useState(null); // { matches, conflictsByLegalHold }
  const [viewing, setViewing] = useState(null); // { legalHold, conflictId } for swap-back view

  const canSearch = (email.trim() || originalUserId.trim()) && reason.trim().length >= 5;

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!canSearch) return;
    setPhase('searching');
    setErrorMessage('');
    setResults(null);
    try {
      const { status, body } = await callCloudFunction('lookupLegalHold', {
        email: email.trim() || undefined,
        originalUserId: originalUserId.trim() || undefined,
        reason: reason.trim(),
      });
      if (status === 200) {
        setResults(body);
        setPhase('results');
      } else {
        setErrorMessage(body?.error || `Server returned ${status}`);
        setPhase('error');
      }
    } catch (e) {
      setErrorMessage(e.message || 'Network error');
      setPhase('error');
    }
  }, [email, originalUserId, reason, canSearch]);

  // Render the swap-back conflict view as an overlay
  if (viewing) {
    return (
      <LegalHoldConflictView
        conflictId={viewing.conflictId}
        legalHold={viewing.legalHold}
        onBack={() => setViewing(null)}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
            <Lock className="w-5 h-5 text-amber-700" />
          </div>
          <div className="text-left">
            <h2 className="text-sm font-semibold text-stone-800">Legal Hold Lookup</h2>
            <p className="text-xs text-stone-500">Search sealed deletion records (subpoena response)</p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
      </button>

      {expanded && (
        <div className="p-4 border-t border-stone-200 bg-stone-50">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4">
            <p className="font-medium mb-1">Audited search</p>
            <p>Every lookup is permanently logged with your identity, timestamp, and the reason you provide below. Only use this for legitimate legal-process requests (subpoena, court order, formal discovery).</p>
          </div>

          <form onSubmit={handleSearch} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Email (preferred)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Original User ID</label>
                <input
                  type="text"
                  value={originalUserId}
                  onChange={(e) => setOriginalUserId(e.target.value)}
                  placeholder="user-1234567890"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">
                Reason for lookup <span className="text-red-600">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Subpoena from Smith v. Jones, Cuyahoga County Court of Common Pleas, Case No. 2026-CV-12345, served 2026-05-16, requesting communications by user@example.com from 2024-01-01 to 2025-12-31."
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <p className="text-[11px] text-stone-500 mt-1">Required. Minimum 5 characters. Recorded permanently in the access log.</p>
            </div>

            <button
              type="submit"
              disabled={!canSearch || phase === 'searching'}
              className="w-full py-2 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              {phase === 'searching' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</>
              ) : (
                <><Search className="w-4 h-4" /> Look up</>
              )}
            </button>
          </form>

          {phase === 'error' && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800">{errorMessage}</p>
            </div>
          )}

          {phase === 'results' && results && (
            <div className="mt-4 space-y-3">
              {(!results.matches || results.matches.length === 0) ? (
                <div className="bg-stone-100 border border-stone-200 rounded-lg p-3 text-center text-sm text-stone-600">
                  No matching legal-hold records found.
                </div>
              ) : (
                <>
                  <p className="text-xs text-stone-500">
                    Found {results.matches.length} match{results.matches.length === 1 ? '' : 'es'}. Lookup has been audit-logged.
                  </p>
                  {results.matches.map((m) => {
                    const conflicts = results.conflictsByLegalHold?.[m.legalHoldId] || [];
                    return (
                      <div key={m.legalHoldId} className="bg-white border border-stone-200 rounded-lg p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div><span className="text-stone-500">Original name:</span> <span className="font-medium text-stone-800">{m.originalName || '—'}</span></div>
                          <div><span className="text-stone-500">Original email:</span> <span className="font-medium text-stone-800">{m.originalEmail || '—'}</span></div>
                          <div><span className="text-stone-500">Original user ID:</span> <span className="font-mono text-stone-700">{m.originalUserId}</span></div>
                          <div><span className="text-stone-500">Original tenant:</span> <span className="font-mono text-stone-700">{m.originalTenantId}</span></div>
                          <div><span className="text-stone-500">Original role:</span> <span className="text-stone-700">{m.originalRole || '—'}</span></div>
                          <div><span className="text-stone-500">Deleted at:</span> <span className="text-stone-700">{m.deletedAt}</span></div>
                          <div className="sm:col-span-2"><span className="text-stone-500">Legal hold ID:</span> <span className="font-mono text-stone-700 text-[11px]">{m.legalHoldId}</span></div>
                          <div className="sm:col-span-2"><span className="text-stone-500">Legal basis:</span> <span className="text-stone-700">{m.legalBasis}</span></div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-stone-200">
                          <p className="text-xs font-medium text-stone-700 mb-2">
                            Conflicts the user participated in ({conflicts.length})
                          </p>
                          {conflicts.length === 0 ? (
                            <p className="text-xs text-stone-500">No conflict participation recorded.</p>
                          ) : (
                            <ul className="space-y-1">
                              {conflicts.map((c) => (
                                <li key={c.id} className="flex items-center justify-between text-xs">
                                  <div className="flex-1 min-w-0 truncate">
                                    <span className="text-stone-800 font-medium">{c.title || '(untitled)'}</span>
                                    <span className="text-stone-500 ml-2">[{c.status || 'in-progress'}]</span>
                                  </div>
                                  <button
                                    onClick={() => setViewing({ legalHold: m, conflictId: c.id })}
                                    className="ml-2 px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded text-[11px] font-medium inline-flex items-center gap-1"
                                  >
                                    <FileText className="w-3 h-3" /> View
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LegalHoldLookupPanel;
